import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/ui/Header';
import LiveStatusIndicator from './components/LiveStatusIndicator';
import AgentCredentials from './components/AgentCredentials';

import MessageInput from './components/MessageInput';
import WelcomeMessage from './components/WelcomeMessage';
import MessageBubble from './components/MessageBubble';
import EmergencyBailAssistanceForm from '../landing-page/components/EmergencyBailAssistanceForm';

import { getSessionId } from '../../services/constants';
import { currentAgentService } from '../../services/currentAgentService';
import { damianChat } from '../../services/damianChatService';

// Add mock database definition
const mockArresteeDatabase = [
  {
    name: "John Doe",
    bookingNumber: "SAC2024001",
    facility: "Sacramento County Main Jail",
    arrestDate: "2024-01-15",
    charges: "DUI",
    bail: "$5,000",
    status: "Active"
  },
  {
    name: "Jane Smith",
    bookingNumber: "RIO2024002",
    facility: "Rio Consumnes Correctional Facility",
    arrestDate: "2024-01-14",
    charges: "Theft",
    bail: "$2,500",
    status: "Active"
  }
];

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId] = useState(getSessionId());
  const [currentAgentInitials, setCurrentAgentInitials] = useState('DS');
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [sessionContext, setSessionContext] = useState(null);
  const [showBailForm, setShowBailForm] = useState(false);
  
  // ✅ NEW: Auto-scroll functionality
  const messagesEndRef = useRef(null);
  
  const scrollToBottom = () => {
    if (messagesEndRef?.current) {
      messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  // ✅ NEW: Auto-scroll after each message
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  // ✅ NEW: Load dynamic agent initials on component mount and when agent changes
  useEffect(() => {
    const loadCurrentAgentInitials = async () => {
      try {
        const initials = await currentAgentService?.getCurrentAgentInitials();
        console.log('📝 Loaded dynamic agent initials:', initials);
        setCurrentAgentInitials(initials);
      } catch (error) {
        console.error('❌ Error loading agent initials:', error);
        setCurrentAgentInitials('DS');
      }
    };

    loadCurrentAgentInitials();

    // Listen for agent changes from real-time subscriptions
    const handleAgentChange = () => {
      console.log('🔄 Agent changed, refreshing initials...');
      loadCurrentAgentInitials();
    };

    window.addEventListener('currentAgentChanged', handleAgentChange);

    return () => {
      window.removeEventListener('currentAgentChanged', handleAgentChange);
    };
  }, []);

  // ✅ ENHANCED: Message handling with proper conversation memory and Claude integration
  const handleSendMessage = async (messageText) => {
    if (!messageText?.trim()) return;
    
    const userMessage = {
      text: messageText,
      content: messageText,
      isUser: true,
      timestamp: new Date()
    };
    
    // ✅ CRITICAL: Add user message first to maintain chronological order
    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);
    setCurrentRequest({ type: 'chat', message: messageText });

    try {
      console.log('🗣️ Sending message with conversation context:', {
        sessionId,
        messageCount: messages?.length + 1,
        currentContext: sessionContext
      });
      
      // ✅ FIXED: Use actual damianChatService with proper conversation memory
      const response = await damianChat?.processMessage(messageText, sessionId);

      if (response?.reply) {
        console.log('✅ Claude/Database response received:', {
          hasReply: !!response?.reply,
          hasDatabaseResults: !!response?.databaseResults,
          searchPerformed: response?.searchPerformed,
          resultCount: response?.databaseResults?.length || 0,
          conversationMemoryEnabled: true // ✅ Now properly enabled
        });
        
        const botMessage = {
          text: response?.reply,
          content: response?.reply,
          isUser: false,
          timestamp: new Date(),
          actions: response?.actions || [],
          databaseResults: response?.databaseResults || null,
          searchPerformed: response?.searchPerformed || false
        };
        
        // ✅ CRITICAL: Append to end to maintain chronological order (oldest → newest)
        setMessages(prev => [...prev, botMessage]);
        
        // ✅ NEW: Update session context for conversation continuity
        setSessionContext(prev => ({
          ...prev,
          lastMessage: messageText,
          lastResponse: response?.reply,
          messageCount: (prev?.messageCount || 0) + 2,
          searchPerformed: response?.searchPerformed,
          hasResults: !!response?.databaseResults?.length
        }));
        
      } else {
        // ✅ CONTEXT-AWARE FALLBACK: No conversation memory loss
        console.log('🔄 Using context-aware fallback response');
        
        let fallbackResponse;
        const lowerMessage = messageText?.toLowerCase();
        const conversationContext = messages?.some(msg => {
          const content = (msg?.text || msg?.content || '')?.toLowerCase();
          return content?.includes('friend') || content?.includes('john') || content?.includes('smith');
        });
        
        if (lowerMessage?.includes('friend') && lowerMessage?.includes('jail') && !conversationContext) {
          fallbackResponse = "What is your friend's name? I can search our Sacramento County database immediately and remember our conversation.";
        } else if (conversationContext) {
          fallbackResponse = "I remember we were talking about your friend. Let me search our database for them right away and check both Sacramento County facilities for current custody status and bail information.";
        } else {
          fallbackResponse = "I'm here to help you search our Sacramento County jail database and provide bail bond assistance. I'll remember our conversation as we go. What specific information do you need?";
        }
        
        const botMessage = {
          text: fallbackResponse,
          content: fallbackResponse,
          isUser: false,
          timestamp: new Date()
        };
        
        // ✅ CRITICAL: Maintain chronological order
        setMessages(prev => [...prev, botMessage]);
      }

    } catch (error) {
      console.error('❌ Error processing message:', error);
      
      // ✅ CONVERSATION-AWARE ERROR RESPONSE
      let errorResponse = "I'm experiencing an issue but I haven't lost track of our conversation. ";
      
      if (messages?.length > 0) {
        errorResponse += "Let me try to help you another way based on what we've discussed, or you can call our office directly for immediate assistance.";
      } else {
        errorResponse += "Please try again or call our office directly for immediate assistance.";
      }
      
      const botMessage = {
        text: errorResponse,
        content: errorResponse,
        isUser: false,
        timestamp: new Date()
      };
      
      // ✅ CRITICAL: Maintain chronological order even for errors
      setMessages(prev => [...prev, botMessage]);
    } finally {
      setIsTyping(false);
      setCurrentRequest(null);
    }
  };

  // Add missing handleCancelRequest function
  const handleCancelRequest = () => {
    setIsTyping(false);
    setCurrentRequest(null);
  };

  // ✅ NEW: Session context header for conversation continuity
  const renderSessionHeader = () => {
    if (sessionContext?.hasResults || sessionContext?.searchPerformed) {
      return (
        <div className="bg-blue-50/80 backdrop-blur-sm border-b border-blue-100 sticky top-[120px] z-30">
          <div className="max-w-6xl mx-auto px-6 py-2">
            <div className="text-sm text-blue-700 font-medium">
              {sessionContext?.hasResults ? (
                `📋 Active Search Session: Found results in database`
              ) : sessionContext?.searchPerformed ? (
                `🔍 Search Session: Checking Sacramento County records`
              ) : (
                `💬 Chat Session: Conversation in progress`
              )}
              <span className="text-blue-600 ml-2">
                ({sessionContext?.messageCount || messages?.length} messages)
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // ✅ NEW: Handler to show bail form
  const handleStartBailProcess = () => {
    setShowBailForm(true);
  };

  // ✅ NEW: Handler to hide bail form
  const handleCloseBailForm = () => {
    setShowBailForm(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex flex-col" key={refreshKey}>
      <Header />
      
      {/* Streamlined Status Bar */}
      <div className="border-b border-slate-200 bg-white/90 backdrop-blur-sm sticky top-0 z-40 shadow-sm">
        <LiveStatusIndicator />
      </div>

      {/* ✅ NEW: Session Context Header for conversation continuity */}
      {renderSessionHeader()}

      {/* Main Chat Container with Proper Message Ordering */}
      <div className="flex-1 flex flex-col max-h-[calc(100vh-80px)]">
        
        {/* Consolidated Agent Section */}
        <div className="bg-white/95 backdrop-blur-sm border-b border-slate-200">
          <div className="max-w-6xl mx-auto px-6 py-4">
            <AgentCredentials />
          </div>
        </div>

        {/* ✅ CONDITIONAL RENDERING: Show either chat or bail form */}
        {showBailForm ? (
          <div className="flex-1 overflow-y-auto bg-gradient-to-b from-slate-50/30 to-white">
            <div className="max-w-5xl mx-auto px-6 py-8">
              {/* Back button */}
              <button
                onClick={handleCloseBailForm}
                className="mb-6 flex items-center text-blue-600 hover:text-blue-800 font-semibold"
              >
                <span className="mr-2">←</span>
                Back to Chat
              </button>
              
              {/* Emergency Bail Form */}
              <EmergencyBailAssistanceForm />
            </div>
          </div>
        ) : (
          <>
            {/* ✅ FIXED: Chat Messages Area - Chronological Order (Oldest → Newest) */}
            {messages?.length === 0 ? (
              <div className="flex-1 relative overflow-hidden bg-gradient-to-b from-slate-50/50 to-white">
                <WelcomeMessage 
                  onSendMessage={handleSendMessage}
                  onStartBailProcess={handleStartBailProcess}
                />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto relative bg-gradient-to-b from-slate-50/30 to-white">
                <div className="relative max-w-6xl mx-auto px-6 py-8 space-y-6">
                  {/* ✅ CRITICAL: Messages render in chronological order (oldest first, newest last) */}
                  {messages?.map((message, index) => (
                    <MessageBubble 
                      key={`${sessionId}-${index}-${message?.timestamp?.getTime() || Date.now()}`}
                      message={message} 
                      isUser={message?.isUser}
                      agentInitials={currentAgentInitials}
                    />
                  ))}
                  
                  {/* ✅ FIXED: Typing indicator with conversation memory context */}
                  {isTyping && (
                    <div className="flex justify-start animate-fade-in">
                      <div className="flex items-center space-x-4 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-2xl px-6 py-4 shadow-lg">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-semibold">{currentAgentInitials}</span>
                        </div>
                        <div className="flex space-x-1.5">
                          <div className="w-2.5 h-2.5 bg-blue-600 rounded-full animate-bounce"></div>
                          <div className="w-2.5 h-2.5 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.15s'}}></div>
                          <div className="w-2.5 h-2.5 bg-blue-600 rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
                        </div>
                        <span className="text-sm text-slate-700 font-medium">
                          {sessionContext?.messageCount > 0 ? 'Thinking about our conversation...' : 'Writing...'}
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* ✅ CRITICAL: Auto-scroll target positioned at bottom */}
                  <div ref={messagesEndRef} className="h-1" />
                </div>
              </div>
            )}

            {/* ✅ REPOSITIONED: Message Input at Bottom */}
            <div className="bg-white/98 backdrop-blur-sm border-t border-slate-200 shadow-lg">
              <div className="max-w-6xl mx-auto">
                <MessageInput 
                  onSendMessage={handleSendMessage} 
                  disabled={isTyping}
                  placeholder={sessionContext?.messageCount > 0 ? "Continue our conversation..." : "How can I help you?"}
                  isTyping={isTyping}
                  onCancelRequest={handleCancelRequest}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;